import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';

import { exportApi } from '../../api/services';
import { reportsCopy } from '../../copy/reports';

// ---------------------------------------------------------------------------
// WeeklyExportDialog — collects an optional leader's note and three narrative
// lines, then triggers the server-rendered weekly PDF download. The note is
// pinned in localStorage so the next export pre-fills with the last sentence
// the executive wrote; the dialog exposes a clear affordance so it can be
// wiped when it no longer applies.
// ---------------------------------------------------------------------------

const PINNED_NOTE_KEY = 'cadence_leader_note_pinned';

function readPinnedNote(): string {
  try {
    return localStorage.getItem(PINNED_NOTE_KEY) ?? '';
  } catch {
    return '';
  }
}

function writePinnedNote(note: string) {
  try {
    if (note.trim()) {
      localStorage.setItem(PINNED_NOTE_KEY, note);
    } else {
      localStorage.removeItem(PINNED_NOTE_KEY);
    }
  } catch {
    // private-browsing / quota-exhausted — fall through.
  }
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on next tick so the click has taken effect.
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
}

function filenameFromContentDisposition(headerValue: string | undefined, fallback: string): string {
  if (!headerValue) return fallback;
  // Handle both RFC 5987 and legacy formats.
  const rfc5987 = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
  if (rfc5987?.[1]) return decodeURIComponent(rfc5987[1].trim().replace(/"/g, ''));
  const legacy = headerValue.match(/filename="?([^"]+)"?/i);
  return legacy?.[1] ?? fallback;
}

interface WeeklyExportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Date within the week being exported (yyyy-MM-dd). */
  date: string;
  /** Auto-derived narrative to pre-fill the three text fields. */
  suggestion: { whatHappened: string; whatItMeans: string; whatsNext: string };
  forExecId?: string;
}

export default function WeeklyExportDialog({
  open,
  onClose,
  date,
  suggestion,
  forExecId,
}: WeeklyExportDialogProps) {
  const [whatHappened, setWhatHappened] = useState(suggestion.whatHappened);
  const [whatItMeans, setWhatItMeans] = useState(suggestion.whatItMeans);
  const [whatsNext, setWhatsNext] = useState(suggestion.whatsNext);
  const [leaderNote, setLeaderNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset fields to the latest suggestion every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setWhatHappened(suggestion.whatHappened);
    setWhatItMeans(suggestion.whatItMeans);
    setWhatsNext(suggestion.whatsNext);
    setLeaderNote(readPinnedNote());
    setError(null);
  }, [open, suggestion]);

  const handleClearPin = () => {
    writePinnedNote('');
    setLeaderNote('');
  };

  const handleExport = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        leaderNote: leaderNote.trim() || undefined,
        whatHappened: whatHappened.trim() || undefined,
        whatItMeans: whatItMeans.trim() || undefined,
        whatsNext: whatsNext.trim() || undefined,
      };
      const response = await exportApi.downloadWeeklySummaryPdf(date, payload, forExecId);
      const blob = response.data as Blob;
      const filename = filenameFromContentDisposition(
        response.headers?.['content-disposition'] as string | undefined,
        `Cadence-WeeklySummary-${date}.pdf`,
      );
      triggerBrowserDownload(blob, filename);

      // Pin the note for the next export only if there's something to pin.
      writePinnedNote(leaderNote);

      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? reportsCopy.exportFailed);
    } finally {
      setBusy(false);
    }
  };

  const hasPinnedNote = readPinnedNote().length > 0;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{reportsCopy.dialogTitle}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
          {reportsCopy.dialogBody}
        </Typography>

        <Stack spacing={2}>
          <TextField
            label={reportsCopy.whatHappenedLabel}
            value={whatHappened}
            onChange={(e) => setWhatHappened(e.target.value)}
            multiline
            minRows={2}
            size="small"
            fullWidth
            disabled={busy}
          />
          <TextField
            label={reportsCopy.whatItMeansLabel}
            value={whatItMeans}
            onChange={(e) => setWhatItMeans(e.target.value)}
            multiline
            minRows={2}
            size="small"
            fullWidth
            disabled={busy}
          />
          <TextField
            label={reportsCopy.whatsNextLabel}
            value={whatsNext}
            onChange={(e) => setWhatsNext(e.target.value)}
            multiline
            minRows={2}
            size="small"
            fullWidth
            disabled={busy}
          />
          <TextField
            label={reportsCopy.leaderNoteLabel}
            placeholder={reportsCopy.leaderNotePlaceholder}
            value={leaderNote}
            onChange={(e) => setLeaderNote(e.target.value)}
            multiline
            minRows={2}
            size="small"
            fullWidth
            disabled={busy}
            helperText={hasPinnedNote ? reportsCopy.pinnedNoteSaved : undefined}
          />
          {hasPinnedNote && (
            <Stack direction="row" justifyContent="flex-end">
              <Button size="small" onClick={handleClearPin} disabled={busy}>
                {reportsCopy.clearPinnedNote}
              </Button>
            </Stack>
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {reportsCopy.cancel}
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
        >
          {busy ? reportsCopy.exportingPdf : reportsCopy.export}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
